/* eslint-disable react/prop-types */
"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import { useBodyLock } from "../../hooks/useDisableBodyScroll";
import { useMailbox } from "../../context/MailboxContext";
import { useAssistantUnread } from "../../context/AssistantUnreadContext";
import { useProfileCardModal } from "../../context/ProfileCardContext";
import { RequiredActionsContext } from "../../context/RequiredActionsContext";
import { CHAT_POLL_INTERVAL_MS } from "../../constants/chatPolling";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";
import { BADGES } from "../../constants/badges";
import SegmentedModeToggle from "../common/SegmentedModeToggle";
import MailboxModal from "./MailboxModal";
import ChatNotificationBellIcon from "./ChatNotificationBellIcon";
import {
  DELETED_USER_LABEL,
  isDeletedAccountUser,
} from "../../utils/deletedAccount";
import { resolveUserAvatarSrc as resolveSharedUserAvatarSrc } from "../../utils/userAvatar";
import { getMailboxAttentionCount } from "../../utils/attentionBadges";

const CHAT_MODAL_SCROLL_TOP_KEY = "vg_open_chat_modal_scroll_top";
const DIRECT_CHATS_PAGE_SIZE = 50;
const GROUP_CHATS_PAGE_SIZE = 50;
const EVENT_CHATS_PAGE_SIZE = 50;
const SUPPORT_CHATS_PAGE_SIZE = 50;
const DIRECT_CHATS_LOAD_MORE_THRESHOLD_PX = 320;
const NEXT_EVENTS_WINDOW_DAYS = 5;
const NEXT_EVENTS_EXCLUDED_STATUSES = new Set([
  "cancelled",
  "deleted",
  "expired_unvalidated",
  "finished",
]);
const SUPPORT_CHAT_AVATAR_SRC = "/assets/menu/support_chat.png";
export const CHAT_FOREGROUND_REFRESH_DELAYS_MS = [0, 750, 2000, 5000];

const CHAT_TABS = [
  { key: "all", labelKey: "chats.tabs.all", fallback: "All" },
  { key: "events", labelKey: "chats.tabs.events", fallback: "Events" },
  { key: "groups", labelKey: "chats.tabs.groups", fallback: "Clubs" },
  { key: "direct", labelKey: "chats.tabs.direct", fallback: "Direct" },
];
const CHAT_TAB_KEYS = new Set(CHAT_TABS.map((tab) => tab.key));
const CHAT_DESKTOP_CONTENT_CLASS =
  "lg:mx-auto lg:w-[min(var(--vg-desktop-feed-content-max),calc(100%_-_96px))]";

function normalizeChatTabKey(value) {
  const key = String(value || "").trim().toLowerCase();
  return CHAT_TAB_KEYS.has(key) ? key : null;
}

function readStoredChatTabKey() {
  if (typeof window === "undefined") return null;
  try {
    return normalizeChatTabKey(window.sessionStorage.getItem("vg_open_chat_modal_tab"));
  } catch {
    return null;
  }
}

function toPositiveInt(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

export function compareChatListRows(a, b) {
  const eventA = a?.pinnedEvent ? 1 : 0;
  const eventB = b?.pinnedEvent ? 1 : 0;
  if (eventA !== eventB) return eventB - eventA;

  const supportA = a?.pinnedSupport ? 1 : 0;
  const supportB = b?.pinnedSupport ? 1 : 0;
  if (supportA !== supportB) return supportB - supportA;

  const buddyA = a?.pinnedBuddy ? 1 : 0;
  const buddyB = b?.pinnedBuddy ? 1 : 0;
  if (buddyA !== buddyB) return buddyB - buddyA;

  const validationA = a?.validationPending ? 1 : 0;
  const validationB = b?.validationPending ? 1 : 0;
  if (validationA !== validationB) return validationB - validationA;

  const unreadA = toPositiveInt(a?.unreadCount) > 0 ? 1 : 0;
  const unreadB = toPositiveInt(b?.unreadCount) > 0 ? 1 : 0;
  if (unreadA !== unreadB) return unreadB - unreadA;
  if (a?.sortTime !== b?.sortTime) return Number(b?.sortTime || 0) - Number(a?.sortTime || 0);
  if (a?.lastMessageId !== b?.lastMessageId) {
    return Number(b?.lastMessageId || 0) - Number(a?.lastMessageId || 0);
  }
  return toPositiveInt(b?.id) - toPositiveInt(a?.id);
}

export function resolveChatListError(results) {
  const list = Array.isArray(results) ? results : [];
  if (!list.length) return null;
  const failedCount = list.filter((result) => result?.status === "rejected").length;
  if (!failedCount) return null;
  return failedCount === list.length ? "failed" : "partial";
}

function waitForChatRefreshRetry(delayMs, signal) {
  if (!delayMs) return Promise.resolve();

  return new Promise((resolve) => {
    const finish = () => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    };
    const timerId = window.setTimeout(finish, delayMs);
    const handleAbort = () => {
      window.clearTimeout(timerId);
      finish();
    };
    signal?.addEventListener("abort", handleAbort, { once: true });
  });
}

export async function retryChatListRefresh(
  refresh,
  {
    delays = CHAT_FOREGROUND_REFRESH_DELAYS_MS,
    signal,
    wait = waitForChatRefreshRetry,
  } = {}
) {
  for (const delayMs of delays) {
    if (signal?.aborted) return false;
    await wait(delayMs, signal);
    if (signal?.aborted) return false;
    if (await refresh()) return true;
  }
  return false;
}

function parseDateMs(value) {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function getNextEventChats(eventChats, now = Date.now()) {
  const max = now + NEXT_EVENTS_WINDOW_DAYS * 86400000;
  return (Array.isArray(eventChats) ? eventChats : [])
    .filter((eventChat) => {
      const status = String(eventChat?.status || "").trim().toLowerCase();
      if (NEXT_EVENTS_EXCLUDED_STATUSES.has(status)) return false;

      const ms = parseDateMs(eventChat?.date_time);
      return ms >= now - 60000 && ms <= max;
    })
    .sort((a, b) => parseDateMs(a?.date_time) - parseDateMs(b?.date_time))
    .slice(0, 10);
}

function getBadgeSrc(label) {
  const badge = BADGES.find((item) => item.id === label);
  return badge?.src || null;
}

function resolveUserAvatarSrc(user) {
  return resolveSharedUserAvatarSrc(user);
}

function resolveEventImageSrc(eventChat) {
  return (
    eventChat?.icon_image_url ||
    getBadgeSrc(eventChat?.badge_label)
  );
}

function resolveNextEventImageSrc(eventChat) {
  return String(
    eventChat?.hero_image_url ||
      eventChat?.cover_image_url ||
      eventChat?.cover_url ||
      eventChat?.event?.hero_image_url ||
      eventChat?.icon_image_url ||
      getBadgeSrc(eventChat?.badge_label) ||
      ""
  ).trim() || null;
}

function isImageAttachment(attachment) {
  const type = String(
    attachment?.type ||
      attachment?.attachment_type ||
      attachment?.mime_type ||
      attachment?.content_type ||
      ""
  )
    .trim()
    .toLowerCase();
  return type === "image" || type.startsWith("image/");
}

function chatMessageHasImageAttachment(message) {
  const attachments = Array.isArray(message?.attachments) ? message.attachments : [];
  return attachments.some(isImageAttachment);
}

function chatRowHasLastMessage(row) {
  return Boolean(
    toPositiveInt(row?.last_message_id) ||
      toPositiveInt(row?.last_message?.id) ||
      toPositiveInt(row?.lastMessageId)
  );
}

function isValidationPendingEventChat(eventChat) {
  return Boolean(eventChat?.validation_pending || eventChat?.is_validation_pending);
}

function getEmptyLastMessagePreview(row, t) {
  if (!chatRowHasLastMessage(row)) return "";
  return t("chats.list.photo_preview", { defaultValue: "Photo" });
}

function resolveGroupAvatarSrc(group) {
  if (group?.club?.hero_image_url) return group.club.hero_image_url;
  if (group?.cohort?.group_kind === "new_joiners") return "/assets/event_pics/new.webp";
  return null;
}

function normalizeUsername(username) {
  return String(username || "").trim().replace(/^@/, "");
}

function formatClock(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatChatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (sameDay) return formatClock(date);

  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "2-digit",
  }).format(date);
}

function formatNextEventTime(value, t) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const eventDay = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const dayDiff = Math.round((eventDay - today) / 86400000);
  const time = formatClock(date);

  if (dayDiff === 0) {
    return `${t("chats.next_events.today", { defaultValue: "Today" })}, ${time}`;
  }
  if (dayDiff === 1) {
    return `${t("chats.next_events.tomorrow", { defaultValue: "Tomorrow" })}, ${time}`;
  }

  const label = new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
  }).format(date);
  return `${label}, ${time}`;
}

function formatUnreadLabel(count) {
  const unread = Number(count || 0);
  if (unread <= 0) return "";
  if (unread > 999) return "999+";
  if (unread > 99) return "99+";
  return String(unread);
}

function ChevronRight() {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5 text-white/45" aria-hidden="true">
      <path
        d="M7.5 4.5L12.5 10L7.5 15.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HeaderGlassButton({ children, onClick, ariaLabel, badgeCount = 0 }) {
  const badgeLabel = formatUnreadLabel(badgeCount);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="vg-glass-control relative flex h-[50px] w-[51px] items-center justify-center rounded-[99px] text-white shadow-[0_1px_0_rgba(0,0,0,0.05),0_4px_4px_rgba(0,0,0,0.05),0_10px_10px_rgba(0,0,0,0.1)] backdrop-blur-[10px] transition active:scale-[0.98]"
    >
      {children}
      {badgeLabel ? (
        <span className="absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-white px-[5px] text-[10px] font-extrabold leading-none text-[#3E6FFB] shadow-[0_0_8px_rgba(255,255,255,0.42)]">
          {badgeLabel}
        </span>
      ) : null}
    </button>
  );
}

function TabUnreadBadge({ count }) {
  const label = formatUnreadLabel(count);
  if (!label) return null;
  return (
    <span className="absolute -right-1 -top-[8px] z-20 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[9px] font-semibold leading-none text-[#3E6FFB]">
      {label}
    </span>
  );
}

function ChatTabs({ selectedTab, onSelect, counts, t, isRu = false }) {
  const options = CHAT_TABS.map((tab) => ({
    value: tab.key,
    label: t(tab.labelKey, { defaultValue: tab.fallback }),
  }));

  return (
    <div className="mx-auto w-full">
      <SegmentedModeToggle
        value={selectedTab}
        options={options}
        onChange={onSelect}
        ariaLabel={t("chats.header.title")}
        className="vg-glass-control h-10 !overflow-visible !rounded-[99px] !border-white/10"
        style={{ width: "100%" }}
        knobClassName="h-[34px] !rounded-[99px] !border-0"
        buttonClassName={`min-w-0 !font-medium !leading-5 !text-white ${
          isRu ? "px-1 !text-[12px]" : "px-2 !text-[13px]"
        }`}
        surfaceStyle={{
          backgroundImage:
            "linear-gradient(180deg,rgba(255,255,255,0.02)_0%,rgba(255,255,255,0.1)_100%)",
        }}
        activeStyle={{
          backgroundImage: "none",
          backgroundColor: "rgba(0,0,0,0.5)",
          boxShadow:
            "0 1px 0 rgba(0,0,0,0.05), 0 4px 4px rgba(0,0,0,0.05), 0 10px 10px rgba(0,0,0,0.1)",
        }}
        measureKnob
        knobInset={3}
        renderAccessory={(option) => <TabUnreadBadge count={counts[option.value]} />}
      />
    </div>
  );
}

function NextEventCard({ eventChat, t, onOpen }) {
  const unreadLabel = formatUnreadLabel(eventChat?.unread_count);
  const title = String(eventChat?.name || "").trim().toUpperCase();
  const imageSrc = resolveNextEventImageSrc(eventChat);

  return (
    <article className="relative h-[100px] w-[140px] shrink-0 overflow-hidden rounded-2xl bg-[linear-gradient(135deg,#A2AAB8_0%,#737D90_52%,#505969_100%)] shadow-[0_12px_24px_rgba(15,31,74,0.18)]">
      {imageSrc ? (
        <>
          <img src={imageSrc} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,17,38,0.80)_0%,rgba(7,17,38,0.55)_50%,rgba(7,17,38,0.72)_100%)]" aria-hidden="true" />
        </>
      ) : (
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_18%,rgba(255,255,255,0.26)_0%,rgba(255,255,255,0)_34%),linear-gradient(135deg,#A8B0BD_0%,#737D8E_54%,#4D5665_100%)]" aria-hidden="true" />
      )}
      <div className="relative z-10 flex h-full flex-col px-[13px] py-[10px] text-white">
        <h3 className="truncate text-[12px] font-bold leading-[16px] tracking-[-0.01em]">{title}</h3>
        <p className="mt-1 truncate text-[10px] font-normal leading-[14px] tracking-[-0.01em]">
          {formatNextEventTime(eventChat?.date_time, t)}
        </p>
        <button
          type="button"
          onClick={onOpen}
          className="mt-auto flex h-[29px] w-full items-center justify-center rounded-[100px] bg-white/95 text-[10px] font-semibold leading-4 text-[#3E6FFB] shadow-[0_8px_16px_rgba(0,0,0,0.18)] transition active:scale-[0.98]"
        >
          {t("chats.next_events.show_chat", { defaultValue: "Show chat" })}
        </button>
      </div>
      {unreadLabel ? (
        <span className="absolute bottom-[31px] right-[7px] z-20 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#4F82FF] px-1 text-[9px] font-semibold leading-none text-white">
          {unreadLabel}
        </span>
      ) : null}
    </article>
  );
}

function NextEventsLoadingSkeleton({ cards = 3 }) {
  return (
    <div className="flex gap-[5px]" aria-hidden="true">
      {Array.from({ length: cards }).map((_, index) => (
        <article
          key={`next-event-skeleton-${index}`}
          className="vg-chat-loading-row relative h-[100px] w-[140px] shrink-0 overflow-hidden rounded-2xl bg-[linear-gradient(135deg,rgba(255,255,255,0.16)_0%,rgba(255,255,255,0.1)_52%,rgba(255,255,255,0.07)_100%)]"
          style={{ animationDelay: `${index * 70}ms` }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_16%,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0)_34%),linear-gradient(180deg,rgba(255,255,255,0.02)_0%,rgba(0,0,0,0.18)_100%)]" />
          <div className="relative z-10 flex h-full flex-col px-[13px] py-[10px]">
            <SkeletonBlock className="h-[16px] w-[82%] bg-white/22" />
            <SkeletonBlock className="mt-[7px] h-[12px] w-[64%] bg-white/16" />
            <SkeletonBlock className="mt-auto h-[29px] w-full rounded-[100px] bg-white/24" />
          </div>
        </article>
      ))}
    </div>
  );
}

function ChatAvatar({ item }) {
  const fallbackLetter = String(item.titleText || item.kind || "C").charAt(0).toUpperCase();
  const direct = item.kind === "direct";
  const radiusClass = direct ? "rounded-full" : "rounded-2xl";
  const borderClass = item.validationPending ? "border-[#FFE15C]/80" : "border-white/20";

  return (
    <div
      className={`h-16 w-16 shrink-0 overflow-hidden border bg-black/35 ${borderClass} ${radiusClass}`}
    >
      {item.imageSrc ? (
        <img
          src={item.imageSrc}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(135deg,#335BCE,#101727)] text-xl font-extrabold text-white">
          {fallbackLetter}
        </div>
      )}
    </div>
  );
}

export function ChatTitle({ item }) {
  if (item.kind !== "direct") {
    return <span>{item.titleText}</span>;
  }

  return (
    <span className="inline-flex min-w-0 items-baseline gap-1.5 text-[15px] leading-[22px] text-white">
      <span className="relative inline-block shrink-0 font-extrabold uppercase leading-[22px]">
        {item.codeLabel}
        {item.hasPremiumSubscription ? (
          <span
            className="pointer-events-none absolute inset-y-[1px] -inset-x-1 rounded-[4px] border border-white"
            aria-hidden="true"
          />
        ) : null}
      </span>
      {item.usernameLabel ? (
        <span className="min-w-0 truncate font-medium uppercase leading-[22px]">
          {item.usernameLabel}
        </span>
      ) : null}
    </span>
  );
}

export function ChatListRow({ item, showDivider, onOpen, onOpenProfile, profileAriaLabel = "", mutedAriaLabel = "" }) {
  const unreadLabel = formatUnreadLabel(item.unreadCount);
  const validationBadgeLabel = String(item.validationBadgeLabel || "").trim();
  const peer = item.kind === "direct" ? item.source?.peer : null;
  const canOpenProfile = Boolean(
    onOpenProfile && peer && !isDeletedAccountUser(peer) && /^[A-Z0-9]{3}$/.test(String(peer.code || "").trim().toUpperCase())
  );

  return (
    <div className="relative">
      <div className="flex h-[90px] w-full items-center gap-[15px]">
        <button
          type="button"
          onClick={canOpenProfile ? onOpenProfile : onOpen}
          aria-label={canOpenProfile ? profileAriaLabel : item.titleText}
          className="h-16 w-16 shrink-0 transition active:scale-[0.98]"
        >
          <ChatAvatar item={item} />
        </button>
        <button
          type="button"
          onClick={onOpen}
          className="flex h-[90px] min-w-0 flex-1 items-center text-left transition active:scale-[0.995]"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-3">
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <h3 className="-mx-[5px] flex min-w-0 items-baseline overflow-hidden px-[5px] text-[15px] leading-[22px] text-white">
                  <ChatTitle item={item} />
                </h3>
                {item.notificationsMuted ? (
                  <span
                    className="inline-flex h-[22px] shrink-0 items-center text-white/65"
                    aria-label={mutedAriaLabel}
                    title={mutedAriaLabel}
                  >
                    <ChatNotificationBellIcon muted size={16} />
                  </span>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1 pt-[1px]">
                {item.timeLabel ? (
                  <span className="text-[14px] font-normal leading-[22px] text-white/50">
                    {item.timeLabel}
                  </span>
                ) : null}
                <ChevronRight />
              </div>
            </div>
            {validationBadgeLabel ? (
              <div className="mt-[5px] flex min-w-0">
                <span className="inline-flex max-w-full items-center truncate rounded-[6px] bg-[#FFE15C] px-2 py-[4px] text-[10px] font-extrabold uppercase leading-none text-[#17213B] shadow-[0_6px_14px_rgba(0,0,0,0.18)]">
                  {validationBadgeLabel}
                </span>
              </div>
            ) : null}
            <div className={`${validationBadgeLabel ? "mt-[4px]" : "mt-[5px]"} flex items-center gap-2`}>
              <p className="min-w-0 flex-1 truncate text-[14px] font-normal leading-[22px] text-white/50">
                {item.previewText}
              </p>
              {unreadLabel ? (
                <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-white px-1.5 text-[12px] font-semibold leading-none text-[#3E6FFB]">
                  {unreadLabel}
                </span>
              ) : null}
            </div>
          </div>
        </button>
      </div>
      {showDivider ? (
        <div className="ml-[79px] h-px bg-white/20" aria-hidden="true" />
      ) : null}
    </div>
  );
}

function SkeletonBlock({ className = "", dark = false }) {
  return (
    <span
      className={`vg-chat-loading-shimmer block rounded-full ${
        dark ? "bg-black/10" : "bg-white/16"
      } ${className}`}
      aria-hidden="true"
    />
  );
}

function ChatListLoadingSkeleton({ rows = 5, compact = false }) {
  return (
    <div className={compact ? "" : "pt-1"} aria-hidden="true">
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={`chat-list-skeleton-${index}`}
          className="vg-chat-loading-row"
          style={{ animationDelay: `${index * 70}ms` }}
        >
          <div className="flex h-[90px] w-full items-center gap-[15px]">
            <SkeletonBlock className="h-16 w-16 shrink-0 rounded-full bg-white/14" />
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-3">
                <SkeletonBlock className="h-[18px] w-[58%] bg-white/22" />
                <SkeletonBlock className="ml-auto h-[14px] w-[46px] bg-white/12" />
              </div>
              <SkeletonBlock className="mt-[13px] h-[14px] w-[76%] bg-white/12" />
            </div>
          </div>
          {index < rows - 1 ? <div className="ml-[79px] h-px bg-white/10" /> : null}
        </div>
      ))}
    </div>
  );
}

function LoadingState() {
  return <ChatListLoadingSkeleton />;
}

export default function ChatsModal({
  visible = true,
  onClose,
  openEventId = null,
  initialTabKey = null,
  initialScrollTop = null,
  transitionBackdrop = false,
  disableEnterAnimation = false,
  asPage = false,
}) {
  const { t, i18n } = useTranslation("menu");
  const isRu = String(i18n?.resolvedLanguage || i18n?.language || "")
    .toLowerCase()
    .startsWith("ru");
  const navigate = useNavigate();
  const { openProfileCard } = useProfileCardModal();
  const mailboxCtx = useMailbox();
  const requiredActionsCtx = React.useContext(RequiredActionsContext);
  const refreshRequiredActions = requiredActionsCtx?.refresh;
  const { unreadCount: assistantUnreadCount } = useAssistantUnread();
  const unreadMailbox = Number(mailboxCtx?.unreadCount ?? 0) || (Array.isArray(mailboxCtx?.notifications)
    ? mailboxCtx.notifications.length
    : 0);
  const pendingRequiredActionsCount = Array.isArray(requiredActionsCtx?.actions)
    ? requiredActionsCtx.actions.length
    : 0;
  const mailboxAttentionCount = getMailboxAttentionCount(
    unreadMailbox,
    pendingRequiredActionsCount
  );

  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= 1024
  );
  const [forceVisible, setForceVisible] = useState(false);
  const [selectedTabKey, setSelectedTabKey] = useState(
    () => normalizeChatTabKey(initialTabKey) || readStoredChatTabKey() || "all"
  );
  const [eventChats, setEventChats] = useState([]);
  const [professionalInvitations, setProfessionalInvitations] = useState([]);
  const [nextEventChats, setNextEventChats] = useState([]);
  const [supportChats, setSupportChats] = useState([]);
  const [directChats, setDirectChats] = useState([]);
  const [groupChats, setGroupChats] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [isLoadingMoreDirect, setIsLoadingMoreDirect] = useState(false);
  const [isLoadingMoreGroup, setIsLoadingMoreGroup] = useState(false);
  const [isLoadingMoreEvent, setIsLoadingMoreEvent] = useState(false);
  const [isLoadingMoreSupport, setIsLoadingMoreSupport] = useState(false);
  const [directOffset, setDirectOffset] = useState(0);
  const [groupOffset, setGroupOffset] = useState(0);
  const [eventOffset, setEventOffset] = useState(0);
  const [supportOffset, setSupportOffset] = useState(0);
  const [hasMoreDirect, setHasMoreDirect] = useState(false);
  const [hasMoreGroup, setHasMoreGroup] = useState(false);
  const [hasMoreEvent, setHasMoreEvent] = useState(false);
  const [hasMoreSupport, setHasMoreSupport] = useState(false);
  const [eventTotalUnreadCount, setEventTotalUnreadCount] = useState(null);
  const [supportTotalUnreadCount, setSupportTotalUnreadCount] = useState(null);
  const [showMailboxModal, setShowMailboxModal] = useState(false);

  const listScrollRef = useRef(null);
  const supportLoadMoreRef = useRef(null);
  const perTabScrollTopRef = useRef({ all: 0, events: 0, groups: 0, direct: 0 });
  const pendingRestoreScrollTopRef = useRef(null);
  const directRequestInFlightRef = useRef(false);
  const groupRequestInFlightRef = useRef(false);
  const eventRequestInFlightRef = useRef(false);
  const supportRequestInFlightRef = useRef(false);

  const effectiveVisible = visible || forceVisible;
  useBodyLock(effectiveVisible && isDesktop && !transitionBackdrop && !asPage);

  useEffect(() => {
    if (!effectiveVisible) return;
    void refreshRequiredActions?.({ silent: true });
  }, [effectiveVisible, refreshRequiredActions]);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    try {
      const shouldOpen = sessionStorage.getItem("vg_open_chat_modal_after_back");
      if (shouldOpen === "1") {
        sessionStorage.removeItem("vg_open_chat_modal_after_back");
        sessionStorage.removeItem("vg_open_chat_modal_chat_id");
        setForceVisible(true);
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  React.useLayoutEffect(() => {
    if (!effectiveVisible) return;

    const restoredTab = normalizeChatTabKey(initialTabKey);

    if (asPage) {
      setSelectedTabKey(restoredTab || "all");
      const parsedScrollTop = Number(restoredTab ? initialScrollTop ?? 0 : 0);
      pendingRestoreScrollTopRef.current =
        Number.isFinite(parsedScrollTop) && parsedScrollTop >= 0 ? parsedScrollTop : 0;
      return;
    }

    if (restoredTab) {
      setSelectedTabKey(restoredTab);
      const parsedScrollTop = Number(initialScrollTop ?? 0);
      pendingRestoreScrollTopRef.current =
        Number.isFinite(parsedScrollTop) && parsedScrollTop >= 0 ? parsedScrollTop : 0;
      return;
    }

    try {
      const validTab = normalizeChatTabKey(sessionStorage.getItem("vg_open_chat_modal_tab"));
      if (validTab) {
        sessionStorage.removeItem("vg_open_chat_modal_tab");
        setSelectedTabKey(validTab);
        const parsedScrollTop = Number(sessionStorage.getItem(CHAT_MODAL_SCROLL_TOP_KEY) || 0);
        sessionStorage.removeItem(CHAT_MODAL_SCROLL_TOP_KEY);
        pendingRestoreScrollTopRef.current =
          Number.isFinite(parsedScrollTop) && parsedScrollTop >= 0 ? parsedScrollTop : 0;
        return;
      }
    } catch {
      // ignore storage errors
    }

    setSelectedTabKey("all");
  }, [asPage, effectiveVisible, initialScrollTop, initialTabKey]);

  const mergeDirectChats = useCallback((current, incoming) => {
    const byId = new Map();
    const push = (row) => {
      const id = row?.chat_id ?? row?.direct_chat_id;
      if (id == null) return;
      byId.set(String(id), row);
    };
    (Array.isArray(current) ? current : []).forEach(push);
    (Array.isArray(incoming) ? incoming : []).forEach(push);
    return Array.from(byId.values());
  }, []);

  const mergeEventChats = useCallback((current, incoming) => {
    const byId = new Map();
    const push = (row) => {
      const id = row?.chat_id ?? row?.event_id;
      if (id == null) return;
      byId.set(String(id), row);
    };
    (Array.isArray(current) ? current : []).forEach(push);
    (Array.isArray(incoming) ? incoming : []).forEach(push);
    return Array.from(byId.values());
  }, []);

  const fetchEventChatsPage = useCallback(
    async ({ offset = 0, append = false, limit = EVENT_CHATS_PAGE_SIZE } = {}) => {
      if (eventRequestInFlightRef.current) return;
      eventRequestInFlightRef.current = true;
      if (append) setIsLoadingMoreEvent(true);

      try {
        const res = await api.get("/chats/event-chats/", {
          params: { limit, offset },
        });
        const payload = res?.data || {};
        const list = payload?.results || [];
        const count = Number(payload?.count);
        const next = payload?.next;
        const totalUnreadCount = Number(payload?.total_unread_count);

        setEventChats((prev) => (append ? mergeEventChats(prev, list) : list));
        setNextEventChats(
          Array.isArray(payload?.next_events) ? payload.next_events : list
        );
        setEventTotalUnreadCount(
          Number.isFinite(totalUnreadCount) ? totalUnreadCount : null
        );

        const fallbackHasMore = Array.isArray(list) && list.length === limit;
        const calculatedHasMore = Number.isFinite(count)
          ? offset + list.length < count
          : Boolean(next) || fallbackHasMore;

        setHasMoreEvent(calculatedHasMore);
        setEventOffset(offset + list.length);
      } finally {
        eventRequestInFlightRef.current = false;
        setIsLoadingMoreEvent(false);
      }
    },
    [mergeEventChats]
  );

  const fetchDirectChatsPage = useCallback(
    async ({ offset = 0, append = false, limit = DIRECT_CHATS_PAGE_SIZE } = {}) => {
      if (directRequestInFlightRef.current) return;
      directRequestInFlightRef.current = true;
      if (append) setIsLoadingMoreDirect(true);

      try {
        const res = await api.get("/chats/direct/", {
          params: { limit, offset },
        });
        const payload = res?.data || {};
        const list = payload?.results || payload?.chats || [];
        const count = Number(payload?.count);
        const next = payload?.next;

        setDirectChats((prev) => (append ? mergeDirectChats(prev, list) : list));

        const fallbackHasMore = Array.isArray(list) && list.length === limit;
        const calculatedHasMore = Number.isFinite(count)
          ? offset + list.length < count
          : Boolean(next) || fallbackHasMore;

        setHasMoreDirect(calculatedHasMore);
        setDirectOffset(offset + list.length);
      } finally {
        directRequestInFlightRef.current = false;
        setIsLoadingMoreDirect(false);
      }
    },
    [mergeDirectChats]
  );

  const mergeGroupChats = useCallback((current, incoming) => {
    const byId = new Map();
    const push = (row) => {
      const id = row?.chat_id ?? row?.id;
      if (id == null) return;
      byId.set(String(id), row);
    };
    (Array.isArray(current) ? current : []).forEach(push);
    (Array.isArray(incoming) ? incoming : []).forEach(push);
    return Array.from(byId.values());
  }, []);

  const fetchGroupChatsPage = useCallback(
    async ({ offset = 0, append = false, limit = GROUP_CHATS_PAGE_SIZE } = {}) => {
      if (groupRequestInFlightRef.current) return;
      groupRequestInFlightRef.current = true;
      if (append) setIsLoadingMoreGroup(true);

      try {
        const res = await api.get("/chats/groups/", {
          params: { limit, offset },
        });
        const payload = res?.data || {};
        const list = payload?.results || [];
        const count = Number(payload?.count);
        const next = payload?.next;

        setGroupChats((prev) => (append ? mergeGroupChats(prev, list) : list));

        const fallbackHasMore = Array.isArray(list) && list.length === limit;
        const calculatedHasMore = Number.isFinite(count)
          ? offset + list.length < count
          : Boolean(next) || fallbackHasMore;

        setHasMoreGroup(calculatedHasMore);
        setGroupOffset(offset + list.length);
      } finally {
        groupRequestInFlightRef.current = false;
        setIsLoadingMoreGroup(false);
      }
    },
    [mergeGroupChats]
  );

  const mergeSupportChats = useCallback((current, incoming) => {
    const byId = new Map();
    const push = (row) => {
      const id = row?.chat_id ?? row?.id;
      if (id == null) return;
      byId.set(String(id), row);
    };
    (Array.isArray(current) ? current : []).forEach(push);
    (Array.isArray(incoming) ? incoming : []).forEach(push);
    return Array.from(byId.values());
  }, []);

  const fetchSupportChatsPage = useCallback(
    async ({ offset = 0, append = false, limit = SUPPORT_CHATS_PAGE_SIZE } = {}) => {
      if (supportRequestInFlightRef.current) return;
      supportRequestInFlightRef.current = true;
      if (append) setIsLoadingMoreSupport(true);

      try {
        const res = await api.get("/chats/support/", {
          params: { limit, offset },
        });
        const payload = res?.data || {};
        const list = payload?.results || [];
        const count = Number(payload?.count);
        const next = payload?.next;
        const totalUnreadCount = Number(payload?.total_unread_count);

        setSupportChats((prev) => (append ? mergeSupportChats(prev, list) : list));
        setSupportTotalUnreadCount(
          Number.isFinite(totalUnreadCount) ? totalUnreadCount : null
        );

        const fallbackHasMore = Array.isArray(list) && list.length === limit;
        const calculatedHasMore = Number.isFinite(count)
          ? offset + list.length < count
          : Boolean(next) || fallbackHasMore;

        setHasMoreSupport(calculatedHasMore);
        setSupportOffset(offset + list.length);
      } finally {
        supportRequestInFlightRef.current = false;
        setIsLoadingMoreSupport(false);
      }
    },
    [mergeSupportChats]
  );

  const loadAllLists = useCallback(async () => {
    setIsInitialLoading(true);
    setListError(null);

    try {
      directRequestInFlightRef.current = false;
      groupRequestInFlightRef.current = false;
      eventRequestInFlightRef.current = false;
      supportRequestInFlightRef.current = false;
      const [eventsResult, groupsResult, supportResult, directResult, invitationsResult] = await Promise.allSettled([
        api.get("/chats/event-chats/", {
          params: { limit: EVENT_CHATS_PAGE_SIZE, offset: 0 },
        }),
        api.get("/chats/groups/", {
          params: { limit: GROUP_CHATS_PAGE_SIZE, offset: 0 },
        }),
        api.get("/chats/support/", {
          params: { limit: SUPPORT_CHATS_PAGE_SIZE, offset: 0 },
        }),
        api.get("/chats/direct/", {
          params: { limit: DIRECT_CHATS_PAGE_SIZE, offset: 0 },
        }),
        api.get("/events/professional-meetup-invitations/"),
      ]);

      if (invitationsResult.status === "fulfilled") {
        setProfessionalInvitations(Array.isArray(invitationsResult.value?.data?.results)
          ? invitationsResult.value.data.results
          : []);
      }

      if (eventsResult.status === "fulfilled") {
        const payload = eventsResult.value?.data || {};
        const list = payload?.results || [];
        const count = Number(payload?.count);
        const totalUnreadCount = Number(payload?.total_unread_count);
        setEventChats(list);
        setNextEventChats(
          Array.isArray(payload?.next_events) ? payload.next_events : list
        );
        setEventOffset(list.length);
        setHasMoreEvent(
          Number.isFinite(count) ? list.length < count : Boolean(payload?.next)
        );
        setEventTotalUnreadCount(
          Number.isFinite(totalUnreadCount) ? totalUnreadCount : null
        );
      }
      if (groupsResult.status === "fulfilled") {
        const payload = groupsResult.value?.data || {};
        const list = payload?.results || [];
        const count = Number(payload?.count);
        setGroupChats(list);
        setGroupOffset(list.length);
        setHasMoreGroup(
          Number.isFinite(count) ? list.length < count : Boolean(payload?.next)
        );
      }
      if (supportResult.status === "fulfilled") {
        const payload = supportResult.value?.data || {};
        const list = payload?.results || [];
        const count = Number(payload?.count);
        const totalUnreadCount = Number(payload?.total_unread_count);
        setSupportChats(list);
        setSupportOffset(list.length);
        setHasMoreSupport(
          Number.isFinite(count) ? list.length < count : Boolean(payload?.next)
        );
        setSupportTotalUnreadCount(
          Number.isFinite(totalUnreadCount) ? totalUnreadCount : null
        );
      }
      if (directResult.status === "fulfilled") {
        const payload = directResult.value?.data || {};
        const list = payload?.results || payload?.chats || [];
        const count = Number(payload?.count);
        setDirectChats(list);
        setDirectOffset(list.length);
        setHasMoreDirect(Number.isFinite(count) ? list.length < count : Boolean(payload?.next));
      }

      setListError(
        resolveChatListError([
          eventsResult,
          groupsResult,
          supportResult,
          directResult,
        ])
      );
    } finally {
      setIsInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!effectiveVisible) return;
    void loadAllLists();
  }, [effectiveVisible, loadAllLists]);

  useEffect(() => {
    if (!effectiveVisible) return;
    if (!openEventId) return;

    navigate(`/menu/chats/event/${openEventId}`, {
      state: {
        openKind: "event",
        openBy: "event_id",
        event_id: openEventId,
        source: "chats_modal",
        returnToChats: { tab: "events", scrollTop: 0 },
      },
    });
  }, [effectiveVisible, navigate, openEventId]);

  useEffect(() => {
    if (!effectiveVisible) return;

    let cancelled = false;

    const refreshChatLists = async () => {
      const directRefreshLimit = Math.max(
        Number(directChats.length || 0),
        DIRECT_CHATS_PAGE_SIZE
      );
      const groupRefreshLimit = Math.max(
        Number(groupChats.length || 0),
        GROUP_CHATS_PAGE_SIZE
      );
      const eventRefreshLimit = Math.max(
        Number(eventChats.length || 0),
        EVENT_CHATS_PAGE_SIZE
      );
      const supportRefreshLimit = Math.max(
        Number(supportChats.length || 0),
        SUPPORT_CHATS_PAGE_SIZE
      );

      const [directResult, eventResult, groupResult, supportResult, invitationsResult] = await Promise.allSettled([
        api.get("/chats/direct/", {
          params: { limit: directRefreshLimit, offset: 0 },
        }),
        api.get("/chats/event-chats/", {
          params: { limit: eventRefreshLimit, offset: 0 },
        }),
        api.get("/chats/groups/", {
          params: { limit: groupRefreshLimit, offset: 0 },
        }),
        api.get("/chats/support/", {
          params: { limit: supportRefreshLimit, offset: 0 },
        }),
        api.get("/events/professional-meetup-invitations/"),
      ]);

      if (cancelled) return;

      if (directResult.status === "fulfilled") {
        const payload = directResult.value?.data || {};
        const list = payload?.results || payload?.chats || [];
        const count = Number(payload?.count);
        setDirectChats(list);
        setDirectOffset(list.length);
        if (Number.isFinite(count)) setHasMoreDirect(list.length < count);
      }
      if (eventResult.status === "fulfilled") {
        const payload = eventResult.value?.data || {};
        const list = payload?.results || [];
        const count = Number(payload?.count);
        const totalUnreadCount = Number(payload?.total_unread_count);
        setEventChats(list);
        setNextEventChats(
          Array.isArray(payload?.next_events) ? payload.next_events : list
        );
        setEventOffset(list.length);
        setHasMoreEvent(
          Number.isFinite(count) ? list.length < count : Boolean(payload?.next)
        );
        setEventTotalUnreadCount(
          Number.isFinite(totalUnreadCount) ? totalUnreadCount : null
        );
      }
      if (groupResult.status === "fulfilled") {
        const payload = groupResult.value?.data || {};
        const list = payload?.results || [];
        const count = Number(payload?.count);
        setGroupChats(list);
        setGroupOffset(list.length);
        if (Number.isFinite(count)) {
          setHasMoreGroup(list.length < count);
        } else {
          setHasMoreGroup(Boolean(payload?.next));
        }
      }
      if (supportResult.status === "fulfilled") {
        const payload = supportResult.value?.data || {};
        const list = payload?.results || [];
        const count = Number(payload?.count);
        const totalUnreadCount = Number(payload?.total_unread_count);
        setSupportChats(list);
        setSupportOffset(list.length);
        setHasMoreSupport(
          Number.isFinite(count) ? list.length < count : Boolean(payload?.next)
        );
        setSupportTotalUnreadCount(
          Number.isFinite(totalUnreadCount) ? totalUnreadCount : null
        );
      }
      if (invitationsResult.status === "fulfilled") {
        setProfessionalInvitations(Array.isArray(invitationsResult.value?.data?.results)
          ? invitationsResult.value.data.results
          : []);
      }

      const refreshError = resolveChatListError([
        directResult,
        eventResult,
        groupResult,
        supportResult,
      ]);
      if (!refreshError) setListError(null);
      return !refreshError;
    };

    let foregroundRefreshPromise = null;
    let foregroundRefreshController = null;

    const refreshAfterForeground = () => {
      if (foregroundRefreshPromise) return foregroundRefreshPromise;

      // Keep the last successfully loaded chats visible while the native/web
      // transport wakes up. A foreground failure is transient and retries
      // automatically instead of asking the user to press "Try again".
      setListError(null);
      foregroundRefreshController = new AbortController();
      const { signal } = foregroundRefreshController;
      foregroundRefreshPromise = retryChatListRefresh(
        () => refreshChatLists(),
        { signal }
      ).finally(() => {
        foregroundRefreshPromise = null;
        foregroundRefreshController = null;
      });
      return foregroundRefreshPromise;
    };

    const timerId = window.setInterval(() => {
      void refreshChatLists();
    }, CHAT_POLL_INTERVAL_MS);

    const handleRealtimeInboxUpdate = () => {
      void refreshChatLists();
    };
    const handleForeground = () => {
      void refreshAfterForeground();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") handleForeground();
    };
    window.addEventListener("vg:chat-inbox-updated", handleRealtimeInboxUpdate);
    window.addEventListener("online", handleForeground);
    window.addEventListener("focus", handleForeground);
    window.addEventListener("vg:native-resume", handleForeground);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      foregroundRefreshController?.abort();
      window.clearInterval(timerId);
      window.removeEventListener("vg:chat-inbox-updated", handleRealtimeInboxUpdate);
      window.removeEventListener("online", handleForeground);
      window.removeEventListener("focus", handleForeground);
      window.removeEventListener("vg:native-resume", handleForeground);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    directChats.length,
    effectiveVisible,
    eventChats.length,
    groupChats.length,
    supportChats.length,
  ]);

  const formatGroupTitle = useCallback(
    (group) => {
      if (group?.club?.name) return String(group.club.name).trim();
      if (group?.cohort?.group_kind === "new_joiners") {
        const rawTitle = String(group?.cohort?.title || group?.name || "").trim();
        if (rawTitle) return rawTitle;
        return group?.cohort?.is_test_mode
          ? t("chats.groups.new_joiners_test_title", { defaultValue: "New Joiners Test" })
          : t("chats.groups.new_joiners_title", { defaultValue: "New Joiners" });
      }
      const rawTitle = String(group?.cohort?.title || group?.name || "Club").trim();
      if (group?.cohort?.is_test_mode) return rawTitle || "Club";
      return rawTitle.replace(/\s+test$/i, "").trim() || "Club";
    },
    [t]
  );

  const normalizedEvents = useMemo(
    () =>
      eventChats.map((eventChat) => {
        const rawPreview = String(eventChat.last_text || "").trim();
        const emptyLastMessagePreview =
          !rawPreview && chatMessageHasImageAttachment(eventChat?.last_message)
            ? t("chats.list.photo_preview", { defaultValue: "Photo" })
            : getEmptyLastMessagePreview(eventChat, t);
        const validationPending = isValidationPendingEventChat(eventChat);
        return {
          kind: "event",
          source: eventChat,
          id: eventChat.event_id,
          chatId: eventChat.chat_id,
          titleText: String(eventChat.name || "").trim().toUpperCase(),
          previewText:
            rawPreview ||
            emptyLastMessagePreview ||
            t("chats.list.no_preview", { defaultValue: "No messages yet." }),
          imageSrc: resolveEventImageSrc(eventChat),
          unreadCount: toPositiveInt(eventChat.unread_count),
          lastMessageId: toPositiveInt(eventChat.last_message_id),
          sortTime: parseDateMs(eventChat?.last_message?.created_at) || parseDateMs(eventChat.date_time),
          timeLabel: formatChatTime(eventChat?.last_message?.created_at),
          validationPending,
          validationBadgeLabel: validationPending
            ? t(
                eventChat.viewer_is_staff_chat_watcher
                  ? "chats.list.validation_pending"
                  : "chats.list.not_published",
                {
                  defaultValue: eventChat.viewer_is_staff_chat_watcher
                    ? "Validation pending"
                    : "Not published",
                }
              )
            : "",
          pinnedEvent: Boolean(eventChat.event_chat_priority),
          notificationsMuted: Boolean(eventChat.notifications_muted),
        };
      }),
    [eventChats, t]
  );

  const meetupInvitationRows = useMemo(() => professionalInvitations
    .filter((invitation) => !invitation.viewer_joined)
    .map((invitation) => ({
      kind: "meetup_invitation",
      source: invitation,
      id: invitation.event_id,
      chatId: invitation.event_id,
      titleText: String(invitation.event?.name || "Marketing Meetup").toUpperCase(),
      previewText: t("chats.professional_meetup.preview", {
        topic: invitation.topic_label,
        defaultValue: "VaultGrid invited you to a professional meetup",
      }),
      imageSrc: resolveEventImageSrc(invitation.event),
      unreadCount: invitation.is_unread ? 1 : 0,
      sortTime: parseDateMs(invitation.created_at),
      timeLabel: "",
    })), [professionalInvitations, t]);

  const normalizedGroups = useMemo(
    () =>
      groupChats.map((groupChat) => {
        const title = formatGroupTitle(groupChat);
        const author = groupChat?.last_message?.author || null;
        const authorCode = String(author?.code || author?.username || "").trim().toUpperCase();
        const rawPreview = String(groupChat.last_text || "").trim();
        const mediaPreview =
          !rawPreview && chatMessageHasImageAttachment(groupChat?.last_message)
            ? t("chats.list.photo_preview", { defaultValue: "Photo" })
            : getEmptyLastMessagePreview(groupChat, t);
        const previewFallback = groupChat?.last_message?.id
          ? t("chats.groups.preview_fallback", { defaultValue: "Message without text" })
          : t("chats.groups.no_preview", { defaultValue: "No messages yet." });
        const previewText = rawPreview || mediaPreview;

        return {
          kind: "group",
          source: groupChat,
          id: groupChat.chat_id ?? groupChat.id,
          chatId: groupChat.chat_id ?? groupChat.id,
          titleText: title,
          previewText: previewText
            ? authorCode
              ? `${authorCode}: ${previewText}`
              : previewText
            : previewFallback,
          imageSrc: resolveGroupAvatarSrc(groupChat),
          unreadCount: toPositiveInt(groupChat.unread_count),
          lastMessageId: toPositiveInt(groupChat.last_message_id),
          sortTime: parseDateMs(groupChat?.last_message?.created_at) || toPositiveInt(groupChat.last_message_id),
          timeLabel: formatChatTime(groupChat?.last_message?.created_at),
          pinnedBuddy: groupChat?.subtype === "buddy" || Boolean(groupChat?.buddy_group),
          notificationsMuted: Boolean(groupChat.notifications_muted),
        };
      }),
    [formatGroupTitle, groupChats, t]
  );

  const normalizedSupport = useMemo(
    () =>
      supportChats.map((supportChat) => {
        const rawPreview = String(supportChat.last_text || "").trim();
        const emptyLastMessagePreview =
          !rawPreview && chatMessageHasImageAttachment(supportChat?.last_message)
            ? t("chats.list.photo_preview", { defaultValue: "Photo" })
            : getEmptyLastMessagePreview(supportChat, t);
        return {
          kind: "support",
          source: supportChat,
          id: supportChat.chat_id ?? supportChat.id,
          chatId: supportChat.chat_id ?? supportChat.id,
          titleText:
            String(supportChat.display_name || supportChat.title || supportChat.name || "").trim() ||
            t("chats.support.title", { defaultValue: "Support" }),
          previewText:
            rawPreview ||
            emptyLastMessagePreview ||
            t("chats.support.preview", {
              defaultValue: "Questions, issues, and help from VaultGrid admins.",
            }),
          imageSrc: supportChat.image_url || supportChat.avatar_url || SUPPORT_CHAT_AVATAR_SRC,
          unreadCount: toPositiveInt(supportChat.unread_count),
          lastMessageId: toPositiveInt(supportChat.last_message_id),
          sortTime:
            parseDateMs(supportChat?.last_message?.created_at) ||
            toPositiveInt(supportChat.last_message_id),
          timeLabel: formatChatTime(supportChat?.last_message?.created_at),
          pinnedSupport: true,
          notificationsMuted: Boolean(supportChat.notifications_muted),
        };
      }),
    [supportChats, t]
  );

  const normalizedDirect = useMemo(
    () =>
      directChats.map((directChat) => {
        const peer = directChat.peer || null;
        const isDeletedPeer = isDeletedAccountUser(peer);
        const code = String(peer?.code || "").trim().toUpperCase();
        const username = normalizeUsername(peer?.username);
        const rawPreview = String(directChat.last_text || "").trim();
        const emptyLastMessagePreview =
          !rawPreview && chatMessageHasImageAttachment(directChat?.last_message)
            ? t("chats.list.photo_preview", { defaultValue: "Photo" })
            : getEmptyLastMessagePreview(directChat, t);
        const hasPremiumSubscription = Boolean(
          !isDeletedPeer &&
            code &&
            (peer?.has_premium_subscription || peer?.is_premium || peer?.isPremium)
        );

        return {
          kind: "direct",
          source: directChat,
          id: directChat.chat_id ?? directChat.direct_chat_id,
          chatId: directChat.chat_id ?? directChat.direct_chat_id,
          titleText: isDeletedPeer ? DELETED_USER_LABEL : code || (username ? `@${username}` : "Direct"),
          codeLabel: isDeletedPeer ? DELETED_USER_LABEL : code || "Direct",
          usernameLabel: isDeletedPeer ? "" : username ? `@${username}` : "",
          hasPremiumSubscription,
          previewText:
            rawPreview ||
            emptyLastMessagePreview ||
            t("chats.list.no_preview", { defaultValue: "No messages yet." }),
          imageSrc: resolveUserAvatarSrc(peer),
          unreadCount: toPositiveInt(
            directChat.unread_count ?? directChat.unread_messages ?? directChat.unread
          ),
          lastMessageId: toPositiveInt(directChat.last_message_id),
          sortTime: parseDateMs(directChat?.last_message?.created_at) || toPositiveInt(directChat.last_message_id),
          timeLabel: formatChatTime(directChat?.last_message?.created_at),
          notificationsMuted: Boolean(directChat.notifications_muted),
        };
      }),
    [directChats, t]
  );

  const allRows = useMemo(() => {
    return [...meetupInvitationRows, ...[...normalizedSupport, ...normalizedEvents, ...normalizedGroups, ...normalizedDirect].sort(
      compareChatListRows
    )];
  }, [meetupInvitationRows, normalizedDirect, normalizedEvents, normalizedGroups, normalizedSupport]);

  const rowsByTab = {
    all: allRows,
    events: [...meetupInvitationRows, ...normalizedEvents],
    groups: normalizedGroups,
    direct: normalizedDirect,
  };

  const loadedEventUnreadCount = [...meetupInvitationRows, ...normalizedEvents].reduce(
    (sum, item) => sum + toPositiveInt(item.unreadCount),
    0
  );
  const resolvedEventUnreadCount = Number.isFinite(eventTotalUnreadCount)
    ? eventTotalUnreadCount + meetupInvitationRows.reduce((sum, item) => sum + toPositiveInt(item.unreadCount), 0)
    : loadedEventUnreadCount;
  const loadedSupportUnreadCount = normalizedSupport.reduce(
    (sum, item) => sum + toPositiveInt(item.unreadCount),
    0
  );
  const resolvedSupportUnreadCount = Number.isFinite(supportTotalUnreadCount)
    ? supportTotalUnreadCount
    : loadedSupportUnreadCount;
  const nonEventUnreadCount = [...normalizedGroups, ...normalizedDirect].reduce(
    (sum, item) => sum + toPositiveInt(item.unreadCount),
    resolvedSupportUnreadCount
  );

  const tabCounts = {
    all: nonEventUnreadCount + resolvedEventUnreadCount,
    events: resolvedEventUnreadCount,
    groups: normalizedGroups.reduce((sum, item) => sum + toPositiveInt(item.unreadCount), 0),
    direct: normalizedDirect.reduce((sum, item) => sum + toPositiveInt(item.unreadCount), 0),
  };

  const nextEvents = useMemo(() => {
    return getNextEventChats(nextEventChats);
  }, [nextEventChats]);

  const currentRows = rowsByTab[selectedTabKey] || allRows;

  useEffect(() => {
    if (!effectiveVisible) return;
    const el = listScrollRef.current;
    if (!el) return;

    const pending = pendingRestoreScrollTopRef.current;
    if (pending != null) {
      el.scrollTop = pending;
      perTabScrollTopRef.current[selectedTabKey] = pending;
      pendingRestoreScrollTopRef.current = null;
      return;
    }

    const saved = Number(perTabScrollTopRef.current[selectedTabKey] || 0);
    el.scrollTop = Number.isFinite(saved) && saved >= 0 ? saved : 0;
  }, [effectiveVisible, selectedTabKey]);

  const rememberCurrentPositionForTab = useCallback((tabKey) => {
    const key = String(tabKey || "").trim() || "all";
    const top = Number(listScrollRef.current?.scrollTop || 0);
    perTabScrollTopRef.current[key] = Number.isFinite(top) && top >= 0 ? top : 0;
  }, []);

  const rememberPositionBeforeOpenChat = useCallback(
    (tabKey) => {
      const key = String(tabKey || "").trim() || "all";
      rememberCurrentPositionForTab(key);
      const savedTop = Number(perTabScrollTopRef.current[key] || 0);
      const normalizedTop = Number.isFinite(savedTop) && savedTop >= 0 ? savedTop : 0;
      try {
        sessionStorage.setItem("vg_open_chat_modal_tab", key);
        sessionStorage.setItem(CHAT_MODAL_SCROLL_TOP_KEY, String(normalizedTop));
      } catch {
        // ignore storage failures
      }
      return normalizedTop;
    },
    [rememberCurrentPositionForTab]
  );

  const handleTabSelect = (tabKey) => {
    rememberCurrentPositionForTab(selectedTabKey);
    setSelectedTabKey(tabKey);
  };

  const openEventChat = useCallback(
    (eventChat, tabKey = selectedTabKey) => {
      const eventId = eventChat?.event_id || eventChat?.id;
      if (!eventId) return;
      const returnScrollTop = rememberPositionBeforeOpenChat(tabKey);
      navigate(`/menu/chats/event/${eventId}`, {
        state: {
          returnToChats: { tab: tabKey, scrollTop: returnScrollTop },
          source: "chats_modal",
        },
      });
    },
    [navigate, rememberPositionBeforeOpenChat, selectedTabKey]
  );

  const openDirectChat = useCallback(
    (directChat, tabKey = selectedTabKey) => {
      const rawId = directChat?.chat_id ?? directChat?.direct_chat_id;
      if (rawId == null) return;
      const s = String(rawId).trim();
      if (!/^\d+$/.test(s)) return;

      const peer = directChat.peer || null;
      const returnScrollTop = rememberPositionBeforeOpenChat(tabKey);
      navigate(`/menu/chats/direct/id/${s}`, {
        state: {
          openKind: "direct",
          openBy: "id",
          chat_id: Number(s),
          returnToChats: { tab: tabKey, scrollTop: returnScrollTop },
          peer: peer
            ? {
                id: peer.id ?? null,
                username: peer.username ?? null,
                code: isDeletedAccountUser(peer)
                  ? null
                  : peer.code
                    ? String(peer.code).trim().toUpperCase()
                    : null,
                level: peer.level ?? peer.storage_level ?? null,
                coins: peer.coins ?? peer.coin_balance ?? null,
                diamonds: peer.diamonds ?? peer.diamond_balance ?? null,
                avatar: isDeletedAccountUser(peer) ? null : peer.avatar ?? null,
                avatar_custom_url: isDeletedAccountUser(peer) ? null : peer.avatar_custom_url ?? null,
                avatar_url: isDeletedAccountUser(peer) ? null : peer.avatar_url ?? null,
                last_seen_at: peer.last_seen_at ?? peer.lastSeenAt ?? null,
                has_premium_subscription: peer.has_premium_subscription ?? peer.is_premium ?? false,
                is_deleted_account: isDeletedAccountUser(peer),
              }
            : null,
          source: "chats_modal",
        },
      });
    },
    [navigate, rememberPositionBeforeOpenChat, selectedTabKey]
  );

  const openGroupChat = useCallback(
    (groupChat, tabKey = selectedTabKey) => {
      const rawId = groupChat?.chat_id ?? groupChat?.id;
      if (rawId == null) return;
      const returnScrollTop = rememberPositionBeforeOpenChat(tabKey);
      navigate(`/menu/chats/group/${encodeURIComponent(String(rawId))}`, {
        state: {
          openKind: "group",
          chat_id: Number(rawId),
          chat: groupChat,
          returnToChats: { tab: tabKey, scrollTop: returnScrollTop },
          source: "chats_modal",
        },
      });
    },
    [navigate, rememberPositionBeforeOpenChat, selectedTabKey]
  );

  const openSupportChat = useCallback(
    (supportChat) => {
      const rawId = supportChat?.chat_id ?? supportChat?.id;
      if (rawId == null) return;
      const s = String(rawId).trim();
      if (!/^\d+$/.test(s)) return;
      const returnScrollTop = rememberPositionBeforeOpenChat("all");
      navigate(`/menu/chats/support/${s}`, {
        state: {
          openKind: "support",
          chat_id: Number(s),
          chat: supportChat,
          returnToChats: { tab: "all", scrollTop: returnScrollTop },
          source: "chats_modal",
        },
      });
    },
    [navigate, rememberPositionBeforeOpenChat]
  );

  const openRow = useCallback(
    (item) => {
      if (!item) return;
      if (item.kind === "event" || item.kind === "meetup_invitation") {
        openEventChat(item.source, selectedTabKey);
      } else if (item.kind === "direct") {
        openDirectChat(item.source, selectedTabKey);
      } else if (item.kind === "support") {
        openSupportChat(item.source, selectedTabKey);
      } else {
        openGroupChat(item.source, selectedTabKey);
      }
    },
    [openDirectChat, openEventChat, openGroupChat, openSupportChat, selectedTabKey]
  );

  const maybeLoadMoreDirectChats = useCallback(() => {
    if (selectedTabKey !== "direct" && selectedTabKey !== "all") return;
    if (!hasMoreDirect || isLoadingMoreDirect) return;
    void fetchDirectChatsPage({ offset: directOffset, append: true });
  }, [
    directOffset,
    fetchDirectChatsPage,
    hasMoreDirect,
    isLoadingMoreDirect,
    selectedTabKey,
  ]);

  const maybeLoadMoreEventChats = useCallback(() => {
    if (selectedTabKey !== "events" && selectedTabKey !== "all") return;
    if (!hasMoreEvent || isLoadingMoreEvent) return;
    void fetchEventChatsPage({ offset: eventOffset, append: true });
  }, [
    eventOffset,
    fetchEventChatsPage,
    hasMoreEvent,
    isLoadingMoreEvent,
    selectedTabKey,
  ]);

  const maybeLoadMoreGroupChats = useCallback(() => {
    if (selectedTabKey !== "groups" && selectedTabKey !== "all") return;
    if (!hasMoreGroup || isLoadingMoreGroup) return;
    void fetchGroupChatsPage({ offset: groupOffset, append: true });
  }, [
    fetchGroupChatsPage,
    groupOffset,
    hasMoreGroup,
    isLoadingMoreGroup,
    selectedTabKey,
  ]);

  const maybeLoadMoreSupportChats = useCallback(() => {
    if (selectedTabKey !== "all") return;
    if (!hasMoreSupport || isLoadingMoreSupport) return;
    void fetchSupportChatsPage({ offset: supportOffset, append: true });
  }, [
    fetchSupportChatsPage,
    hasMoreSupport,
    isLoadingMoreSupport,
    selectedTabKey,
    supportOffset,
  ]);

  useEffect(() => {
    if (!effectiveVisible || selectedTabKey !== "all" || !hasMoreSupport) return undefined;
    const root = listScrollRef.current;
    const target = supportLoadMoreRef.current;
    if (!root || !target || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) maybeLoadMoreSupportChats();
    }, { root, rootMargin: `0px 0px ${DIRECT_CHATS_LOAD_MORE_THRESHOLD_PX}px 0px` });
    observer.observe(target);
    return () => observer.disconnect();
  }, [effectiveVisible, hasMoreSupport, maybeLoadMoreSupportChats, selectedTabKey, supportChats.length]);

  const handleClose = () => {
    setForceVisible(false);
    onClose?.();
  };

  if (!effectiveVisible) return null;

  const bottomSafeSpaceCss =
    asPage && isDesktop ? "42px" : "calc(var(--bottom-nav-clearance, 112px) + 42px)";
  const emptyCopy =
    selectedTabKey === "all"
      ? t("chats.empty.all", { defaultValue: "Here you will see all your chats." })
      : selectedTabKey === "events"
      ? t("chats.empty.events")
      : selectedTabKey === "direct"
      ? t("chats.empty.direct")
      : t("chats.empty.groups");
  const modalAnimationClass = asPage || disableEnterAnimation
    ? ""
    : transitionBackdrop
    ? "vg-chats-modal-underlay-enter"
    : "vg-chats-modal-enter";
  const modalBoxSizeClass = asPage
    ? "h-full w-full max-w-[760px] lg:max-w-none lg:rounded-none"
    : "h-[100dvh] w-screen lg:h-[min(874px,calc(100vh-80px))] lg:w-[402px] lg:rounded-[45px]";
  const asPageContentClass = asPage
    ? CHAT_DESKTOP_CONTENT_CLASS
    : "";
  const headerSurfaceClass = asPage
    ? "absolute inset-x-0 top-0 h-[var(--vg-desktop-top-panel-height)] rounded-b-[45px] bg-gradient-to-r from-[rgba(13,24,55,0.58)] to-[rgba(37,69,157,0.58)] shadow-[0_20px_34px_rgba(0,0,0,0.18)] backdrop-blur-[34px] lg:rounded-b-[var(--vg-desktop-top-panel-radius)]"
    : "absolute inset-x-0 top-0 h-[calc(92px+var(--vg-safe-area-top))] rounded-b-[45px] bg-gradient-to-r from-[rgba(13,24,55,0.58)] to-[rgba(37,69,157,0.58)] shadow-[0_20px_34px_rgba(0,0,0,0.18)] backdrop-blur-[34px]";
  const headerClass = asPage
    ? "pointer-events-none absolute inset-x-0 top-0 z-20 h-[var(--vg-desktop-top-panel-height)] px-[var(--vg-page-gutter)] lg:left-1/2 lg:right-auto lg:w-[var(--vg-desktop-top-panel-width)] lg:-translate-x-1/2 lg:px-0"
    : "relative z-20 h-[calc(92px+var(--vg-safe-area-top))] shrink-0 px-[var(--vg-page-gutter)]";
  const headerRowClass = asPage
    ? "pointer-events-auto relative mx-auto flex w-full max-w-[var(--vg-mobile-shell-max)] items-center justify-between pt-[calc(10px+var(--vg-safe-area-top))] lg:max-w-none lg:px-5 lg:pt-[10px]"
    : "relative mx-auto flex w-full max-w-[var(--vg-mobile-shell-max)] items-center justify-between pt-[calc(10px+var(--vg-safe-area-top))]";

  if (asPage && showMailboxModal) {
    return (
      <MailboxModal
        visible
        asPage
        onClose={() => setShowMailboxModal(false)}
      />
    );
  }

  const ModalBox = (
    <div
      className={`${modalAnimationClass} relative flex ${modalBoxSizeClass} flex-col overflow-hidden text-white`}
      style={{
        ...AUTH_GRADIENT_STYLE,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[170px] bg-[linear-gradient(0deg,rgba(0,0,0,0.95)_0%,rgba(0,0,0,0.72)_42%,rgba(0,0,0,0)_100%)]" />

      <header className={headerClass}>
        <div
          className={headerSurfaceClass}
          aria-hidden="true"
        />
        <div className={headerRowClass}>
          <HeaderGlassButton
            ariaLabel={t("main.menu_layout.cards.assistant_title", { defaultValue: "AI assistant" })}
            badgeCount={assistantUnreadCount}
            onClick={() => navigate("/assistant/core")}
          >
            <span className="text-[15px] font-extrabold italic leading-4 text-white">AI</span>
            <img src="/assets/menu/ai_stars.svg" alt="" className="ml-px h-[11px] w-[11px]" />
          </HeaderGlassButton>

          <h1 className="truncate text-center text-[32px] font-semibold leading-[35px] text-white">
            {t("chats.header.title")}
          </h1>

          <HeaderGlassButton
            ariaLabel={t("main.header.mailbox_alt", { defaultValue: "Mailbox" })}
            badgeCount={mailboxAttentionCount}
            onClick={() => setShowMailboxModal(true)}
          >
            <img src="/assets/menu/mailbox_icon.svg" alt="" className="h-5 w-5" />
          </HeaderGlassButton>
        </div>
      </header>

      <main
        ref={listScrollRef}
        className={`relative z-10 min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-[var(--vg-page-gutter)] pb-0 [-webkit-overflow-scrolling:touch] ${asPage ? "pt-[var(--vg-desktop-top-content-offset)] lg:px-0 custom-scrollbar" : "pt-[20px]"}`}
        style={{
          paddingBottom: bottomSafeSpaceCss,
          scrollPaddingBottom: bottomSafeSpaceCss,
        }}
        onScroll={() => {
          const el = listScrollRef.current;
          const top = Number(el?.scrollTop || 0);
          perTabScrollTopRef.current[selectedTabKey] =
            Number.isFinite(top) && top >= 0 ? top : 0;

          if (!el) return;
          const distanceToBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
          if (distanceToBottom <= DIRECT_CHATS_LOAD_MORE_THRESHOLD_PX) {
            maybeLoadMoreDirectChats();
            maybeLoadMoreGroupChats();
            maybeLoadMoreEventChats();
          }
          const supportEnd = supportLoadMoreRef.current;
          if (selectedTabKey === "all" && supportEnd) {
            const distanceToSupportEnd =
              supportEnd.getBoundingClientRect().top - el.getBoundingClientRect().bottom;
            if (distanceToSupportEnd <= DIRECT_CHATS_LOAD_MORE_THRESHOLD_PX) {
              maybeLoadMoreSupportChats();
            }
          }
        }}
      >
        <div className={asPageContentClass}>
        <section className="mb-[29px]" aria-label={t("chats.next_events.title", { defaultValue: "Next events" })}>
          <h2 className="mb-[6px] text-[24px] font-semibold leading-[22px] tracking-[-0.01em]">
            {t("chats.next_events.title", { defaultValue: "Next events" })}
          </h2>
          <div className="-mx-[var(--vg-page-gutter)] overflow-x-auto px-[var(--vg-page-gutter)] pb-[2px] pt-[2px] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {isInitialLoading ? (
              <NextEventsLoadingSkeleton />
            ) : nextEvents.length ? (
              <div className="flex gap-[5px]">
                {nextEvents.map((eventChat) => (
                  <NextEventCard
                    key={`next-event-${eventChat.chat_id || eventChat.event_id}`}
                    eventChat={eventChat}
                    t={t}
                    onOpen={() => openEventChat(eventChat, selectedTabKey)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex h-[100px] items-center rounded-2xl border border-white/10 bg-white/[0.04] px-4 text-[13px] font-medium leading-5 text-white/70">
                {t("chats.next_events.empty", { defaultValue: "No events in the next 5 days." })}
              </div>
            )}
          </div>
        </section>

        <div className="sticky top-0 z-20 -mx-[2px] mb-[17px] pt-[1px]">
          <ChatTabs
            selectedTab={selectedTabKey}
            onSelect={handleTabSelect}
            counts={tabCounts}
            t={t}
            isRu={isRu}
          />
        </div>

        <section aria-label={t("chats.header.title")}>
          {isInitialLoading ? <LoadingState /> : null}

          {!isInitialLoading && listError ? (
            <div
              role="alert"
              className="mb-3 flex min-h-[96px] flex-col items-center justify-center rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-center text-[13px] font-medium text-red-100"
            >
              <p>
                {listError === "failed"
                  ? t("chats.list.failed")
                  : t("chats.list.partial_failed", {
                      defaultValue: "Some chats could not be refreshed.",
                    })}
              </p>
              <button
                type="button"
                onClick={() => void loadAllLists()}
                className="mt-3 min-h-11 rounded-full border border-white/20 bg-white/10 px-5 text-[13px] font-semibold text-white transition active:scale-[0.98]"
              >
                {t("chats.list.retry", { defaultValue: "Try again" })}
              </button>
            </div>
          ) : null}

          {!isInitialLoading && !currentRows.length && !listError ? (
            <div className="flex min-h-[220px] items-center justify-center px-2 text-center">
              <p className="max-w-[260px] text-[17px] font-semibold leading-6 text-white/82">
                {emptyCopy}
              </p>
            </div>
          ) : null}

          {!isInitialLoading && currentRows.length ? (
            <div>
              {currentRows.map((item, index) => (
                <React.Fragment key={`${item.kind}-${item.chatId || item.id || index}`}>
                  {item.kind === "meetup_invitation" && index === 0 ? (
                    <div className="mb-1 mt-3 flex items-center gap-3">
                      <span aria-hidden="true" className="h-px flex-1 bg-white/25" />
                      <span className="shrink-0 text-center text-[12px] font-semibold text-white/70">
                        {t("chats.professional_meetup.section", { defaultValue: "Your Professional Meetup" })}
                      </span>
                      <span aria-hidden="true" className="h-px flex-1 bg-white/25" />
                    </div>
                  ) : null}
                  <ChatListRow
                    item={item}
                    showDivider={index < currentRows.length - 1 && (
                      item.kind !== "meetup_invitation" ||
                      currentRows[index + 1]?.kind === "meetup_invitation"
                    )}
                    mutedAriaLabel={t("chats.notifications.muted_aria")}
                    onOpen={() => openRow(item)}
                    onOpenProfile={item.kind === "direct" ? () => openProfileCard(item.source.peer) : undefined}
                    profileAriaLabel={t("chats.direct_chat.open_profile_aria")}
                  />
                  {item.kind === "meetup_invitation" && currentRows[index + 1]?.kind !== "meetup_invitation" ? (
                    <div className="my-3 h-px bg-white/25" />
                  ) : null}
                  {selectedTabKey === "all" && item.kind === "support" && currentRows[index + 1]?.kind !== "support" ? (
                    <div ref={supportLoadMoreRef} data-testid="support-page-end" className="h-px" aria-hidden="true" />
                  ) : null}
                </React.Fragment>
              ))}
              {isLoadingMoreDirect ||
              isLoadingMoreGroup ||
              isLoadingMoreEvent ||
              isLoadingMoreSupport ? (
                <ChatListLoadingSkeleton rows={1} compact />
              ) : null}
            </div>
          ) : null}
        </section>
        </div>
      </main>

      {showMailboxModal && !asPage ? <MailboxModal onClose={() => setShowMailboxModal(false)} /> : null}
    </div>
  );

  if (asPage) {
    return (
      <main className="flex h-full min-h-0 w-full items-stretch justify-center overflow-hidden lg:block">
        <div className="relative flex h-full min-h-0 w-full justify-center lg:block">
          {ModalBox}
        </div>
      </main>
    );
  }

  if (!isDesktop) {
    return (
      <div
        className={`fixed inset-0 ${
          transitionBackdrop ? "z-10 pointer-events-none touch-none" : "z-50 pointer-events-auto touch-auto"
        }`}
      >
        <div className={`w-full ${transitionBackdrop ? "pointer-events-none" : "pointer-events-auto"}`}>
          {ModalBox}
        </div>
      </div>
    );
  }

  if (transitionBackdrop) {
    return (
      <main className="absolute inset-0 z-10 flex pointer-events-none items-center justify-center px-6 py-6">
        <div className="relative">{ModalBox}</div>
      </main>
    );
  }

  const desktopModal = (
    <main className="absolute inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        onClick={handleClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        aria-label={t("chats.header.close_aria")}
      />
      <div className="relative z-50">{ModalBox}</div>
    </main>
  );

  const root = document.getElementById("modal-root");
  return root ? createPortal(desktopModal, root) : desktopModal;
}
